import {
  LayoutDashboard,
  Server,
  Activity,
  ArrowLeftRight,
  type LucideIcon,
} from 'lucide-react';

export interface VmwareNavEntry {
  label: string;
  /** Path segment under `/services/compute/:serviceId` ('' = console root). */
  slug: string;
  icon: LucideIcon;
}

export interface VmwareNavGroup {
  heading?: string;
  items: VmwareNavEntry[];
}

/**
 * VMware console navigation — Dashboard and Instances only. VMware does NOT
 * expose Volumes, Snapshots, VPC, Load Balancers, SSH Keys, or Certificates.
 */
export const VMWARE_NAV_GROUPS: VmwareNavGroup[] = [
  {
    items: [
      { label: 'Dashboard', slug: '', icon: LayoutDashboard },
      { label: 'Instances', slug: 'instances', icon: Server },
    ],
  },
];

/**
 * Instance-scope sidebar entries for VMware — shown inside `/instances/:id/*`.
 * Slugs must match the nested routes in InstanceDetail.
 */
export const VMWARE_INSTANCE_NAV: VmwareNavEntry[] = [
  { label: 'Overview', slug: '', icon: LayoutDashboard },
  { label: 'Monitoring', slug: 'monitoring', icon: Activity },
  { label: 'Network traffic', slug: 'network-traffic', icon: ArrowLeftRight },
];

/** Absolute href for a VMware nav entry. */
export function vmwareHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/**
 * Active nav entry href — longest prefix match so `/instances/:id/...`
 * highlights Instances and the bare base highlights Dashboard.
 */
export function vmwareActiveId(base: string, pathname: string): string {
  const all = VMWARE_NAV_GROUPS.flatMap((g) => g.items);
  const matches = all
    .map((e) => vmwareHref(base, e.slug))
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
